import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Box, Button, Card, CardMedia, Grid, Typography } from '@mui/material'
import { toastHelper } from '../../../util/toastHelper'
import { VisuallyHiddenInput } from '../../uploadFile/VisuallyHiddenFileInput'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import Style from '../../molecules/VenderModal/VendorModal.module.scss'
import Stepper from '../../atoms/Stepper/Stepper'
import { GeneralPaymentPopup } from '../GeneralPaymentPopup'
import { LoadingSpinner } from '../../loadingSpinner/LoadingSpinner'
import { BUSINESS_PROFILE_TOTAL_STEPS } from '../../../util/constants'
import { DEFAULT_IMAGE_OPTIONS, getImageFilesValidationError } from '../../../util/imageUploadValidation'
import { useVendorOrPlannerSubscriptionAccess } from '../../../hooks'

const IMAGE_UPLOAD_ACCEPT = DEFAULT_IMAGE_OPTIONS.allowedExtensions.join(',')

export const AddPhotoModal = ({
	step,
	setStep,
	handleFileChange,
	formData,
	setFormData,
	onUpdateProfile,
	saving = false,
}) => {
	const subscriptionAccess = useVendorOrPlannerSubscriptionAccess()
	const [showPaymentPopup, setShowPaymentPopup] = useState(false)

	const maxPhotos = subscriptionAccess?.maxMedia || 1
	const currentCount = formData?.media?.length || 0
	const remainingSlots = Math.max(maxPhotos - currentCount, 0)

	const isBusinessPro = useMemo(() => ['business-pro', 'business-pro-month'].includes(subscriptionAccess?.payment), [subscriptionAccess?.payment])

	const removePicture = (indexToRemove) => {
		const image = formData.media[indexToRemove]

		setFormData({
			...formData,
			containsImage: 'media',
			media: formData.media.filter((_, idx) => idx !== indexToRemove),
			imageMediaMap: { ...formData.imageMediaMap, [image]: null },
		})
	}

	const openPaywall = (message) => {
		if (message) {
			toastHelper(message, 'info')
		}
		if (!isBusinessPro) {
			setShowPaymentPopup(true)
		}
	}

	const handleTieredFileChange = (e) => {
		const files = Array.from(e.target.files || [])
		if (!files.length) return

		if (remainingSlots <= 0) {
			openPaywall(
				`You've reached the ${maxPhotos} photo limit for your current plan.`
			)
			e.target.value = ''
			return
		}

		if (files.length > remainingSlots) {
			openPaywall(
				`You can only add ${remainingSlots} more photo${
					remainingSlots === 1 ? '' : 's'
				} for your current plan.`
			)
			e.target.value = ''
			return
		}

		const validationError = getImageFilesValidationError(files)
		if (validationError) {
			toastHelper(validationError, 'error')
			e.target.value = ''
			return
		}

		handleFileChange(e, 2)
		e.target.value = ''
	}

	const handleDropzoneClick = () => {
		if (remainingSlots <= 0) {
			// If they click when already at max, open paywall immediately
			openPaywall(
				`You've reached the ${maxPhotos} photo limit for your current plan.`
			)
		}
	}

	const preventDragDefaults = (e) => {
		e.preventDefault()
		e.stopPropagation()
	}

	const handleDrop = (e) => {
		preventDragDefaults(e)
		const files = e.dataTransfer?.files
		if (!files?.length) return
		handleTieredFileChange({ target: { files, value: '' } })
	}

	const loading = useSelector((s) => s.auth.loading)

	return (
		<>
			<Grid
				size={{ xs: 12, sm: 12 }}
				className={Style.formContainer}
				sx={{
					paddingRight: '18px !important',
					paddingLeft: '18px !important',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '30px',
						minHeight: '80%',
						height: '100%',
					}}
				>
					{/* Title */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
						}}
						className={Style.titleContainer}
					>
						<Typography
							variant="h3"
							gutterBottom
							className={Style.title}
						>
							Add Photo
						</Typography>
						<Typography
							color="#7ca6bd"
							variant="h6"
							className={Style.modalSemiTitle}
						>
							Add a photo of your business
						</Typography>
					</Box>

					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(172px, 1fr))',
							gap: '16px',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
						}}
					>
						<Button
							sx={{
								width: '172px',
								height: '172px',
								borderRadius: '12px',
								aspectRatio: '1 / 1',
								backgroundColor: 'white',
								textTransform: 'none',
								padding: '0',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								':hover': {
									backgroundColor: '#f4f4f4ff',
								},
								'@media (max-width: 499px)': {
									margin: '0 auto',
								},
							}}
							component="label"
							variant="outlined"
							onClick={handleDropzoneClick}
							onDragEnter={preventDragDefaults}
							onDragOver={preventDragDefaults}
							onDrop={handleDrop}
						>
							<img
								data-cy="photo-dropzone"
								src="/images/DragDrop.svg"
								alt=""
								style={{ width: '28px', height: '28px' }}
							/>
							<Typography
								variant="body1"
								sx={{
									color: '#7CA6BD',
									textAlign: 'center',
									marginTop: '8px',
									font: 'var(--body-font-three)',
								}}
							>
								<Typography
									component="span"
									sx={{
										font: 'var(--body-font-three)',
										fontWeight: 'bold',
									}}
								>
									Drag and drop
								</Typography>
								<br />
								or
								<br />
								click to upload photos
							</Typography>
							<VisuallyHiddenInput
								type="file"
								accept={IMAGE_UPLOAD_ACCEPT}
								onChange={handleTieredFileChange}
								multiple
							/>
						</Button>

						{formData.media?.map((pic, idx) => (
							<Card
								key={idx}
								variant="outlined"
								sx={{
									width: '172px',
									height: '172px',
									borderRadius: '12px',
									aspectRatio: '1 / 1',
									borderColor: '#DFE4EB',
									position: 'relative',
									overflow: 'unset',
									'@media (max-width: 499px)': {
										margin: '0 auto',
									},
								}}
							>
								<CardMedia
									sx={{
										height: '100%',
										borderRadius: '12px',
									}}
									image={pic}
								/>
								<button
									style={{
										position: 'absolute',
										top: '-6px',
										right: '-6px',
										backgroundColor: 'white',
										borderRadius: '50%',
										padding: '0',
									}}
									onClick={() => removePicture(idx)}
								>
									<img src="/images/ImageRemove.svg" alt="" />
								</button>
							</Card>
						))}
					</Box>

					<Box
						className={Style.buttonContainer}
						sx={{
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								'@media (max-width: 499px)': {
									display: 'none',
								},
							}}
						>
							<Stepper
								totalSteps={BUSINESS_PROFILE_TOTAL_STEPS}
								subSteps={1}
								activeStep={step}
							/>
						</Box>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '16px',
								'@media (max-width: 499px)': {
									justifyContent: 'space-between',
									width: '100%',
								},
							}}
						>
							<ButtonCustom
								onClick={() => setStep((step) => step - 1)}
								className="backButton"
								disabled={loading}
							>
								Previous
							</ButtonCustom>
							<ButtonCustom
								disabled={loading || saving}
								onClick={() => {
									// Detect any changes: either new files to upload or removals (null values)
									const hasChanges =
										Object.keys(
											formData.imageMediaMap || {}
										).length > 0
									if (hasChanges) {
										onUpdateProfile(() =>
											setStep((step) => step + 1)
										)
									} else {
										// If no changes, skip the API call and move to next step
									setStep((step) => step + 1)
									}
								}}
								sx={{ minWidth: '85px' }}
							>
								{loading ? (
									<LoadingSpinner size="18px" color="white" />) : saving ? (
									'Saving…'
								) : (
									'Next'
								)}
							</ButtonCustom>
						</Box>
					</Box>
					<Box
						sx={{
							justifyContent: 'center',
							alignItems: 'center',
							display: 'none',
							'@media (max-width: 499px)': { display: 'flex' },
						}}
					>
						<Stepper totalSteps={BUSINESS_PROFILE_TOTAL_STEPS} subSteps={1} activeStep={step} />
					</Box>
				</Box>
			</Grid>

			{showPaymentPopup && (
			<GeneralPaymentPopup
				clickHandler={() => setShowPaymentPopup(false)}
			/>)} 
		</>)}
